import { Col, Form, Input, Select } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";

import { KAPG_COUNTRY_OPTIONS } from "./kapgCountryOptions";
import { AlignedFormRow } from "./styles";
import { TaxRegistrationAddressType } from "./types";
import { xmlSafeTextRule } from "./validation";

interface KapgAddressFieldsProps {
  form: FormInstance;
  /** Name relative to the surrounding `Form.List` item. */
  name: (string | number)[];
  /** The same address, absolute, for reading and clearing values. */
  path: (string | number)[];
  required?: boolean;
}

/** Cleared when the address switches, so the two variants never coexist. */
const FOREIGN_ONLY_KEYS = ["addressExtra", "foreignZip", "country"];
const DOMESTIC_ONLY_KEYS = ["zip"];

/**
 * The address of a shareholder or a legal representative (Zeilen 28 to 30 and
 * 74 to 76a).
 *
 * Domestic and foreign are separate shapes in the ELSTER contract, not one
 * shape with optional fields: a foreign address carries `foreignZip` and a
 * `country`, and carrying `zip` as well is rejected. So switching the type
 * clears the other variant's answers rather than hiding them.
 */
export const KapgAddressFields = ({
  form,
  name,
  path,
  required = false,
}: KapgAddressFieldsProps) => {
  const { t } = useTranslation("TaxRegistration");
  const addressType =
    Form.useWatch([...path, "type"], form) ??
    TaxRegistrationAddressType.DOMESTIC;
  const isForeign = addressType === TaxRegistrationAddressType.FOREIGN;
  const field = (key: string) => t(`kapg.fields.${key}`);
  const sharedField = (key: string) => t(`fields.${key}`);
  const textRules = (label: string) => [
    xmlSafeTextRule(
      (codePoint) =>
        t("validation.invalidCharacter", { field: label, codePoint }) ?? ""
    ),
  ];

  return (
    <AlignedFormRow gutter={16}>
      <Col span={6}>
        <Form.Item
          name={[...name, "type"]}
          label={field("addressType")}
          initialValue={TaxRegistrationAddressType.DOMESTIC}
        >
          <Select
            onChange={(value) => {
              const clearedKeys =
                value === TaxRegistrationAddressType.FOREIGN
                  ? DOMESTIC_ONLY_KEYS
                  : FOREIGN_ONLY_KEYS;
              form.setFields(
                clearedKeys.map((key) => ({
                  name: [...path, key],
                  value: undefined,
                }))
              );
            }}
            options={[
              {
                label: t("kapg.options.addressDomestic"),
                value: TaxRegistrationAddressType.DOMESTIC,
              },
              {
                label: t("kapg.options.addressForeign"),
                value: TaxRegistrationAddressType.FOREIGN,
              },
            ]}
          />
        </Form.Item>
      </Col>
      <Col span={10}>
        <Form.Item
          required={required}
          name={[...name, "street"]}
          label={sharedField("street")}
          rules={textRules(sharedField("street"))}
        >
          <Input />
        </Form.Item>
      </Col>
      {isForeign ? (
        <Col span={4}>
          <Form.Item
            required={required}
            name={[...name, "foreignZip"]}
            label={field("foreignZip")}
            rules={textRules(field("foreignZip"))}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Col>
      ) : (
        <Col span={4}>
          <Form.Item
            required={required}
            name={[...name, "zip"]}
            label={sharedField("zip")}
            rules={textRules(sharedField("zip"))}
          >
            <Input maxLength={20} />
          </Form.Item>
        </Col>
      )}
      <Col span={4}>
        <Form.Item
          required={required}
          name={[...name, "city"]}
          label={sharedField("city")}
          rules={textRules(sharedField("city"))}
        >
          <Input maxLength={120} />
        </Form.Item>
      </Col>
      {isForeign && (
        <>
          <Col span={6}>
            {/* An ELSTER StaatId, not free text: the operator picks a name and
                the 3-digit code is what is stored. A code outside the list is
                only rejected by ERiC, so it cannot be typed. */}
            <Form.Item
              required={required}
              name={[...name, "country"]}
              label={field("country")}
            >
              <Select
                allowClear
                optionFilterProp="label"
                options={[...KAPG_COUNTRY_OPTIONS]}
                showSearch
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              name={[...name, "addressExtra"]}
              label={field("addressExtra")}
              rules={textRules(field("addressExtra"))}
            >
              <Input maxLength={120} />
            </Form.Item>
          </Col>
        </>
      )}
    </AlignedFormRow>
  );
};
